<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import LegalFormDuplicateWarning from '@/components/app/cases/LegalFormDuplicateWarning.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase as Case } from '@/types/generated'
  import { CompanyLegalFormEnum, EntityTypeEnum, TitleEnum } from '@/types/generated'
  import { Building2, ChevronRightIcon, Landmark, User } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'

  const { t } = useTranslation({
    en: {
      client: 'Client Data',
      opponent: 'Opponent Data',
      reset: 'Reset',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution',
      contact: 'Contact Person',
      hasInsurance: 'Has Legal Insurance',
      insuranceNumber: 'Insurance Number',
      insuranceName: 'Insurance Company',
      legalInsurance: 'Legal Insurance',
      // Form Fields
      entityType: 'Type',
      title: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: 'Company Name',
      companyLegalForm: 'Legal Form',
      institutionName: 'Institution Name',
      phone: 'Phone',
      email: 'Email',
      dateOfBirth: 'Date of Birth',
      street: 'Street',
      streetNumber: 'Street Number',
      addressLine2: 'Address Line 2',
      zip: 'ZIP Code',
      city: 'City',
      viewAllData: 'View All Data'
    },
    de: {
      client: 'Mandant',
      opponent: 'Gegner',
      reset: 'Zurücksetzen',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution',
      contact: 'Ansprechpartner',
      hasInsurance: 'Hat Rechtsschutzversicherung',
      insuranceNumber: 'Versicherungsnummer',
      insuranceName: 'Name der Versicherung',
      legalInsurance: 'Rechtsschutzversicherung',
      // Form Fields
      entityType: 'Typ',
      title: 'Anrede',
      firstName: 'Vorname',
      lastName: 'Nachname',
      companyName: 'Firmenname',
      companyLegalForm: 'Rechtsform',
      institutionName: 'Name der Institution',
      phone: 'Telefon',
      email: 'E-Mail',
      dateOfBirth: 'Geburtsdatum',
      street: 'Straße',
      streetNumber: 'Hausnummer',
      addressLine2: 'Adresszeile 2',
      zip: 'PLZ',
      city: 'Stadt',
      viewAllData: 'Alle Daten anzeigen'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const router = useRouter()

  // Type options for SelectField
  const typeOptions = computed(() => [
    {
      value: EntityTypeEnum.NATURAL_PERSON,
      title: t('naturalPerson'),
      icon: User
    },
    {
      value: EntityTypeEnum.LEGAL_PERSON,
      title: t('legalPerson'),
      icon: Building2
    },
    {
      value: EntityTypeEnum.INSTITUTION,
      title: t('institution'),
      icon: Landmark
    }
  ])

  // Title options for SelectField
  const titleOptions = computed(() =>
    Object.entries(TitleEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  // Legal form options for SelectField
  const legalFormOptions = computed(() =>
    Object.entries(CompanyLegalFormEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  // Debounced save function to prevent too many API calls
  // TODO: Consider Using Autosaving form component or making it work with this setup
  const saveOpponentData = debounce(async (key: string, value: any) => {
    try {
      const success = await caseStore.updateOpponentField(key, value)

      if (!success) {
        console.error('Failed to update opponent field:', key)
      }
    } catch (error) {
      console.error('Error saving entity:', error)
    }
  }, 1000)

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    if (!caseRecord.value?.data?.opponent) return

    // Save the value to the API using the store method
    saveOpponentData(field, value)
  }

  // Check if field has AI source
  const hasAISource = (field: string): boolean => {
    return caseStore.hasOpponentFieldAISource(field)
  }

  const viewAllData = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/entities`)
    }
  }

  const handleFixCompanyName = async () => {
    if (!caseRecord.value?.data?.opponent) return

    const companyName = caseRecord.value.data.opponent.company_name || ''
    const legalForm = caseRecord.value.data.opponent.company_legal_form || ''

    if (!companyName || !legalForm) return

    const pattern = new RegExp(
      `[\\s,\\-]*${legalForm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      'i'
    )
    const cleanedName = companyName.replace(pattern, '').trim()

    if (cleanedName) {
      caseRecord.value.data.opponent.company_name = cleanedName
      await caseStore.updateOpponentField('company_name', cleanedName)
    }
  }
</script>

<template>
  <div class="opponent-data-widget">
    <!-- Form Fields -->
    <div class="">
      <!-- Type -->
      <div class="mb-3">
        <SelectField
          v-if="caseRecord?.data"
          v-model="caseRecord.data.opponent.entity_type"
          :options="typeOptions"
          :label="t('type')"
          name="entity_type"
          class="w-full"
          @update:model-value="handleFieldChange('entity_type', $event)"
        >
          <template #prefix>
            <span class="mr-2 text-gray-500">
              <component
                :is="
                  typeOptions.find(
                    (option) => option.value === caseRecord?.data?.opponent.entity_type
                  )?.icon || User
                "
                class="h-4 w-4"
              />
            </span>
          </template>
        </SelectField>
      </div>

      <!-- LEGAL PERSON FIELDS -->
      <template v-if="caseRecord?.data?.opponent?.entity_type === EntityTypeEnum.LEGAL_PERSON">
        <!-- Company Name and Legal Form -->
        <div class="-mx-2 mb-3 flex flex-wrap">
          <div class="mb-3 w-full px-2 sm:mb-0 sm:w-3/4">
            <InputField
              v-if="caseRecord?.data?.opponent"
              v-model="caseRecord.data.opponent.company_name"
              :label="t('companyName')"
              :ai-source="hasAISource('company_name')"
              name="company_name"
              class="w-full"
              @update:model-value="handleFieldChange('company_name', $event)"
            />
          </div>
          <div class="w-full px-2 sm:w-1/4">
            <SelectField
              v-if="caseRecord?.data?.opponent"
              v-model="caseRecord.data.opponent.company_legal_form"
              :options="legalFormOptions"
              :label="t('companyLegalForm')"
              :ai-source="hasAISource('company_legal_form')"
              name="company_legal_form"
              class="w-full"
              @update:model-value="handleFieldChange('company_legal_form', $event)"
            />
          </div>
        </div>

        <LegalFormDuplicateWarning
          :company-name="caseRecord?.data?.opponent?.company_name || ''"
          :legal-form="caseRecord?.data?.opponent?.company_legal_form || ''"
          :on-fix="handleFixCompanyName"
        />

        <!-- Contact Person Section -->
      </template>

      <!-- INSTITUTION FIELDS -->
      <template v-else-if="caseRecord?.data?.opponent?.entity_type === EntityTypeEnum.INSTITUTION">
        <!-- Institution Name -->
        <div class="-mx-2 mb-3 flex">
          <div class="w-full px-2">
            <InputField
              v-if="caseRecord?.data?.opponent?.entity_type === EntityTypeEnum.INSTITUTION"
              v-model="caseRecord.data.opponent.institution_name"
              :label="t('institutionName')"
              :ai-source="hasAISource('institution_name')"
              name="institution_name"
              class="w-full"
              @update:model-value="handleFieldChange('institution_name', $event)"
            />
          </div>
        </div>

        <!-- Contact Person Section -->
      </template>

      <!-- COMMON FIELDS FOR ALL ENTITY TYPES OR NATURAL PERSON ONLY -->
      <!-- Title, First Name, Last Name in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="xs:w-1/2 mb-3 w-full px-2 sm:mb-0 sm:w-1/3">
          <SelectField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.title"
            :options="titleOptions"
            :label="t('title')"
            :ai-source="hasAISource('title')"
            :select-option-text="t('title')"
            name="title"
            placeholder="Nicht ausgewählt"
            class="w-full"
            @update:model-value="handleFieldChange('title', $event)"
          />
        </div>
        <div class="mb-3 w-1/2 px-2 sm:mb-0 sm:w-1/3">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.first_name"
            :label="t('firstName')"
            :ai-source="hasAISource('first_name')"
            name="first_name"
            class="w-full"
            @update:model-value="handleFieldChange('first_name', $event)"
          />
        </div>
        <div class="xs:w-1/2 w-full px-2 sm:w-5/12">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.last_name"
            :label="t('lastName')"
            :ai-source="hasAISource('last_name')"
            name="last_name"
            class="w-full"
            @update:model-value="handleFieldChange('last_name', $event)"
          />
        </div>
      </div>

      <!-- Email and Phone in a row -->
      <div class="-mx-2 mb-3 flex flex-wrap">
        <div class="mb-3 w-full px-2 sm:mb-0 sm:w-1/2">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.email"
            :label="t('email')"
            :ai-source="hasAISource('email')"
            name="email"
            type="email"
            class="w-full"
            @update:model-value="handleFieldChange('email', $event)"
          />
        </div>
        <div class="w-full px-2 sm:w-1/2">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.phone"
            :label="t('phone')"
            :ai-source="hasAISource('phone')"
            name="phone"
            class="w-full"
            @update:model-value="handleFieldChange('phone', $event)"
          />
        </div>
      </div>

      <!-- Address Section - Common for all entity types -->

      <!-- Street and House Number in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="mb-3 w-full px-2 sm:mb-0 sm:w-3/4">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.street"
            :label="t('street')"
            :ai-source="hasAISource('street')"
            name="street"
            class="w-full"
            @update:model-value="handleFieldChange('street', $event)"
          />
        </div>
        <div class="w-full min-w-[33%] px-2 sm:w-1/4">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.street_number"
            :label="t('houseNumber')"
            :ai-source="hasAISource('street_number')"
            name="street_number"
            class="w-full"
            @update:model-value="handleFieldChange('street_number', $event)"
          />
        </div>
      </div>

      <!-- ZIP and City in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="mb-3 min-w-[33%] grow-0 px-2 sm:mb-0">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.zip"
            :label="t('zip')"
            :ai-source="hasAISource('zip')"
            name="zip"
            class="w-full"
            @update:model-value="handleFieldChange('zip', $event)"
          />
        </div>
        <div class="xs:w-2/3 w-full grow px-2">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.city"
            :label="t('city')"
            :ai-source="hasAISource('city')"
            name="city"
            class="w-full"
            @update:model-value="handleFieldChange('city', $event)"
          />
        </div>
      </div>

      <!-- Address Line 2 field -->
      <div class="-mx-2 mb-3 flex">
        <div class="w-full px-2 sm:w-1/3">
          <InputField
            v-if="caseRecord?.data?.opponent"
            v-model="caseRecord.data.opponent.address_line_2"
            :label="t('addressLine2')"
            :ai-source="hasAISource('address_line_2')"
            name="address_line_2"
            class="w-full"
            @update:model-value="handleFieldChange('address_line_2', $event)"
          />
        </div>
      </div>

      <!-- View All Data Link -->
      <div class="mt-4 text-right">
        <a
          href="#"
          class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
          @click.prevent="viewAllData"
        >
          {{ t('viewAllData') }}
          <ChevronRightIcon class="ml-1 h-4 w-4" />
        </a>
      </div>
    </div>
  </div>
</template>
<style scoped>
  .dots-menu {
    letter-spacing: -1px;
    transform: rotate(90deg);
    display: inline-block;
    font-weight: bold;
    font-size: 14px;
  }
</style>
